'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  RecaptchaVerifier,
  signInWithPhoneNumber
} from 'firebase/auth';
import { auth, googleProvider } from './firebaseConfig';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore';
import usePageTitle from './utils/usePageTitle';

// Helper to redirect users based on premium eligibility
async function redirectAfterAuth(user) {
  try {
    // Detect and store user's country on login (non-blocking)
    try {
      await fetch('/api/detectAndStoreCountry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: user.uid })
      });
      console.log('Country detection and storage completed for user login');
    } catch (countryError) {
      console.warn('Country detection failed (non-blocking):', countryError);
      // This is non-blocking - user can still continue
    }

    // Call 10DLC management service on user login
    try {
      await fetch('/api/user-login-10dlc-service', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: user.uid })
      });
      console.log('10DLC assignments processed for user login');
    } catch (dlcError) {
      console.warn('10DLC assignment failed (non-blocking):', dlcError);
      // This is non-blocking - user can still continue
    }

    const res = await fetch('/api/checkPremiumEligibility', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid: user.uid })
    });
    const { eligible } = await res.json();
    window.location.href = eligible ? '/overview' : '/welcome';
  } catch (err) {
    console.error('Premium check error:', err);
    window.location.href = '/overview';
  }
}

// Helper to update user's phone number in database
async function updateUserPhoneNumber(userId, phoneNumber) {
  try {
    const db = getFirestore();
    const userAliasRef = doc(db, 'userAliases', userId);
    
    // Check if document exists first
    const docSnap = await getDoc(userAliasRef);
    if (docSnap.exists()) {
      // Update only the forwardToNumber field, don't touch any other fields
      await setDoc(userAliasRef, { forwardToNumber: phoneNumber }, { merge: true });
      console.log('Phone number saved to user profile');
    } else {
      // If document doesn't exist, create it with the phone number
      await setDoc(userAliasRef, { 
        forwardToNumber: phoneNumber,
        forwardTo: '' // Set empty string for forwardTo if using phone auth
      });
      console.log('Created new user profile with phone number');
    }
  } catch (error) {
    console.error('Error updating phone number:', error);
  }
}

export default function SignIn() {
  // Use the custom hook to set the page title
  usePageTitle('Sign In');
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [isLogin, setIsLogin] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [showPhoneAuth, setShowPhoneAuth] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const recaptchaRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  const functions = getFunctions();
  const createUserRelaysHTTP = httpsCallable(functions, 'createUserRelays');

  // Set the language for SMS messages
  useEffect(() => {
    if (auth) {
      auth.languageCode = 'en';
    }
  }, []);

  // Check if viewport is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const setupRecaptcha = () => {
    try {
      console.log('Setting up reCAPTCHA');
      
      // Clear any existing recaptcha verifier
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }

      // Initialize it with a dedicated container element
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', 
        {
          size: 'normal', // Use normal size for better visibility
          callback: () => {
            console.log('reCAPTCHA solved!');
          },
          'expired-callback': () => {
            console.log('reCAPTCHA expired');
            // Reset the verifier if expired
            if (window.recaptchaVerifier) {
              window.recaptchaVerifier.clear();
              window.recaptchaVerifier = null;
            }
            setMessage('reCAPTCHA expired. Please try again.');
          }
        }
      );
      
      console.log('reCAPTCHA setup complete');
      return true;
    } catch (error) {
      console.error('Error setting up reCAPTCHA:', error);
      setMessage(`Error setting up verification: ${error.message}`);
      return false;
    }
  };

  const createRelaysForNewUser = useCallback(
    async (user) => {
      try {
        console.log('Creating relays for new user:', user);
        
        // Handle phone auth case
        if (user.phoneNumber) {
          console.log('User authenticated with phone number:', user.phoneNumber);
          
          // Call the createUserRelays with an optional email
          const relayResult = await createUserRelaysHTTP({ 
            email: user.email || '', // Pass empty string if no email
            phoneNumber: user.phoneNumber // Add phone number parameter
          });
          
          console.log('Relay creation for phone auth:', relayResult.data.message);
          
          // Make sure the phone number is saved to the user's profile
          await updateUserPhoneNumber(user.uid, user.phoneNumber);
        } else if (user.email) {
          // Standard email auth case
          const relayResult = await createUserRelaysHTTP({ email: user.email });
          console.log('Relay creation for email auth:', relayResult.data.message);
        } else {
          console.error('User has neither email nor phone number');
        }
      } catch (error) {
        console.error('Error calling relay creation function:', error);
        
        // Despite the error, attempt to update the phone number if available
        if (user.phoneNumber) {
          try {
            await updateUserPhoneNumber(user.uid, user.phoneNumber);
            console.log('Saved phone number despite relay creation error');
          } catch (phoneError) {
            console.error('Failed to save phone number after relay error:', phoneError);
          }
        }
      }
    },
    [createUserRelaysHTTP]
  );

  const handlePasswordSignIn = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setMessage('Please enter both email and password.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (isLogin) {
        // Sign in existing user
        const result = await signInWithEmailAndPassword(auth, email, password);
        setMessage('Sign in successful!');
        await redirectAfterAuth(result.user);
      } else {
        // Create new user
        const result = await createUserWithEmailAndPassword(auth, email, password);
        setMessage('Account created successfully!');
        await createRelaysForNewUser(result.user);
        await redirectAfterAuth(result.user);
      }
    } catch (error) {
      setMessage(`Error: ${error.message}`);
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsSubmitting(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      setMessage('Google sign in successful!');
      await createRelaysForNewUser(result.user);
      await redirectAfterAuth(result.user);
    } catch (error) {
      setMessage(`Google sign in error: ${error.message}`);
      setIsSubmitting(false);
    }
  };

  const handleSendVerificationCode = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber || phoneNumber.length < 10) {
      setMessage('Please enter a valid phone number with country code.');
      return;
    }

    try {
      setMessage('Setting up verification...');
      
      // Setup reCAPTCHA
      const setupSuccess = setupRecaptcha();
      if (!setupSuccess) {
        return;
      }

      // Wait a moment for reCAPTCHA to fully initialize
      await new Promise(resolve => setTimeout(resolve, 500));

      if (!window.recaptchaVerifier) {
        setMessage('Verification setup failed. Please try again.');
        return;
      }

      console.log('Sending SMS to:', phoneNumber);
      setMessage('Sending verification code...');

      const confirmationResult = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        window.recaptchaVerifier
      );

      console.log('SMS sent successfully');
      window.confirmationResult = confirmationResult;
      setCodeSent(true);
      setMessage('Verification code sent! Check your phone.');
    } catch (error) {
      console.error('Error sending SMS:', error);
      
      // Clear reCAPTCHA on error
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
          window.recaptchaVerifier = null;
        } catch (clearError) {
          console.error('Error clearing reCAPTCHA:', clearError);
        }
      }

      let errorMessage = 'Error sending verification code. ';
      
      if (error.code === 'auth/too-many-requests') {
        errorMessage += 'Too many attempts. Please try again later.';
      } else if (error.code === 'auth/invalid-phone-number') {
        errorMessage += 'Invalid phone number format. Please include country code (e.g., +1 for US).';
      } else if (error.code === 'auth/missing-phone-number') {
        errorMessage += 'Please enter a phone number.';
      } else if (error.code === 'auth/captcha-check-failed') {
        errorMessage += 'reCAPTCHA verification failed. Please try again.';
      } else {
        errorMessage += error.message;
      }
      
      setMessage(errorMessage);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    if (!verificationCode || verificationCode.length !== 6) {
      setMessage('Please enter the 6-digit verification code.');
      return;
    }

    if (!window.confirmationResult) {
      setMessage('No verification in progress. Please request a new code.');
      return;
    }

    setIsVerifying(true);
    
    try {
      console.log('Verifying code:', verificationCode);
      setMessage('Verifying code...');
      
      const result = await window.confirmationResult.confirm(verificationCode);
      
      console.log('Phone verification successful:', result.user);
      setMessage('Phone verification successful!');
      
      // Create relays for the new phone user
      await createRelaysForNewUser(result.user);
      
      // Clean up
      window.confirmationResult = null;
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
          window.recaptchaVerifier = null;
        } catch (clearError) {
          console.error('Error clearing reCAPTCHA:', clearError);
        }
      }
      
      await redirectAfterAuth(result.user);
    } catch (error) {
      console.error('Verification error:', error);
      setIsVerifying(false);
      
      let errorMessage = 'Verification failed. ';
      
      if (error.code === 'auth/invalid-verification-code') {
        errorMessage += 'Invalid verification code. Please check and try again.';
      } else if (error.code === 'auth/code-expired') {
        errorMessage += 'Verification code expired. Please request a new one.';
      } else {
        errorMessage += error.message;
      }
      
      setMessage(errorMessage);
    }
  };

  const renderPhoneAuth = () => {
    if (!showPhoneAuth) {
      return null;
    }

    if (!codeSent) {
      return (
        <form onSubmit={handleSendVerificationCode} className="mt-6">
          <p className="text-sm mb-4 text-white/80 font-medium">
            Enter your phone number with country code (e.g., +1 for US)
          </p>
          <input
            type="tel"
            placeholder="Phone number (e.g. +15555555555)"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            className="w-full p-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/60 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50 transition-all mb-4"
          />
          <button
            id="send-code-button"
            type="submit"
            className="w-full bg-blue-500/20 hover:bg-blue-500/30 backdrop-blur-sm border border-blue-400/30 px-6 py-4 rounded-xl text-white font-medium transition-all duration-300 hover:scale-105"
            style={{
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(59, 130, 246, 0.2)'
            }}
          >
            Send Verification Code
          </button>
          <div id="recaptcha-container" className="mt-4 flex justify-center"></div>
        </form>
      );
    }

    // The verification code form
    return (
      <div className="mt-6">
        <form onSubmit={(e) => {
          e.preventDefault();
          console.log("Form submitted");
          handleVerifyCode(e);
        }}>
          <p className="text-sm mb-4 text-white/80 font-medium">
            Enter the 6-digit code sent to your phone
          </p>
          <input
            type="text"
            placeholder="Enter 6-digit verification code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            className="w-full p-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/60 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50 transition-all mb-4"
            disabled={isVerifying}
            maxLength={6}
            pattern="[0-9]{6}"
          />
          <button
            type="submit"
            className={`w-full ${isVerifying ? 'bg-gray-500/20 border-gray-400/30' : 'bg-blue-500/20 hover:bg-blue-500/30 border-blue-400/30 hover:scale-105'} backdrop-blur-sm border px-6 py-4 rounded-xl text-white font-medium transition-all duration-300 mb-4`}
            disabled={isVerifying}
            style={{
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(59, 130, 246, 0.2)'
            }}
          >
            {isVerifying ? 'Verifying...' : 'Verify Code'}
          </button>
        </form>
        
        <p className="text-center text-sm">
          <button
            type="button"
            onClick={() => {
              console.log("Reset phone verification");
              setCodeSent(false);
              setVerificationCode('');
              setIsVerifying(false);
              // Clean up when going back
              if (window.recaptchaVerifier) {
                try {
                  window.recaptchaVerifier.clear();
                  window.recaptchaVerifier = null;
                } catch (err) {
                  console.error('Error clearing reCAPTCHA:', err);
                }
              }
            }}
            className="text-blue-300 hover:text-blue-200 transition-colors"
            disabled={isVerifying}
          >
            Use a different phone number
          </button>
        </p>
      </div>
    );
  };

  const loginContent = (
    <>
      <div className={`w-full max-w-md bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 ${isMobile ? 'p-4 mb-3' : 'p-6 mb-4'} shadow-2xl`}
           style={{
             boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
           }}>
        <form onSubmit={handlePasswordSignIn} className="space-y-3">
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`w-full ${isMobile ? 'p-3' : 'p-4'} rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/60 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50 transition-all`}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={`w-full ${isMobile ? 'p-3' : 'p-4'} rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/60 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50 transition-all`}
          />
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full ${isSubmitting ? 'bg-gray-500/20 border-gray-400/30 cursor-not-allowed' : 'bg-blue-500/20 hover:bg-blue-500/30 hover:scale-105'} backdrop-blur-sm border border-blue-400/30 ${isMobile ? 'px-4 py-3' : 'px-6 py-4'} rounded-xl text-white font-medium transition-all duration-300 mb-2`}
            style={{
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(59, 130, 246, 0.2)'
            }}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <span>{isLogin ? 'Signing In...' : 'Creating Account...'}</span>
                <svg className="animate-spin ml-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : (
              isLogin ? 'Sign In' : 'Create an account'
            )}
          </button>
          <p className="text-center text-sm">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-blue-300 hover:text-blue-200 transition-colors"
              disabled={isSubmitting}
            >
              {isLogin ? 'Need an account? Sign up' : 'Already have an account? Sign in'}
            </button>
          </p>
        </form>
      </div>

      <div className={`${isMobile ? 'mt-3' : 'mt-4'} w-full max-w-md flex items-center justify-center gap-4`}>
        <div className="w-full border-t border-white/20"></div>
        <div className="text-xs font-medium whitespace-nowrap text-white/70">Or continue with</div>
        <div className="w-full border-t border-white/20"></div>
      </div>

      <div className={`w-full max-w-md bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 ${isMobile ? 'p-4 mt-3' : 'p-6 mt-4'} shadow-2xl`}
           style={{
             boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
           }}>
        <div className="flex flex-col gap-3">
          <button
            onClick={() => {
              setShowPhoneAuth(!showPhoneAuth);
              if (showPhoneAuth) {
                // Clean up when toggling off
                if (window.recaptchaVerifier) {
                  try {
                    window.recaptchaVerifier.clear();
                    window.recaptchaVerifier = null;
                  } catch (err) {
                    console.error('Error clearing reCAPTCHA:', err);
                  }
                }
                setCodeSent(false);
                setVerificationCode('');
              }
            }}
            className={`flex items-center justify-center gap-3 bg-green-500/20 hover:bg-green-500/30 backdrop-blur-sm border border-green-400/30 ${isMobile ? 'px-4 py-3' : 'px-6 py-4'} rounded-xl text-white font-medium transition-all duration-300 hover:scale-105`}
            style={{
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(34, 197, 94, 0.2)'
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
            </svg>
            <span>Sign in with Phone</span>
          </button>
          
          <button
            onClick={handleGoogleSignIn}
            disabled={isSubmitting}
            className={`flex items-center justify-center gap-3 ${isSubmitting ? 'bg-gray-500/20 border-gray-400/30 cursor-not-allowed' : 'bg-indigo-500/20 hover:bg-indigo-500/30 hover:scale-105'} backdrop-blur-sm border border-indigo-400/30 ${isMobile ? 'px-4 py-3' : 'px-6 py-4'} rounded-xl text-white font-medium transition-all duration-300`}
            style={{
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(99, 102, 241, 0.2)'
            }}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Signing In...</span>
              </>
            ) : (
              <>
                <img
                  src="/images/google-icon-logo.svg"
                  alt="Google Logo"
                  className="h-5 w-5"
                />
                <span>Sign in with Google</span>
              </>
            )}
          </button>
        </div>
        
        {renderPhoneAuth()}
      </div>

      {message && (
        <div className={`${isMobile ? 'mt-3' : 'mt-4'} text-center font-medium text-white bg-white/10 backdrop-blur-sm border border-white/20 ${isMobile ? 'py-2 px-4' : 'py-3 px-6'} rounded-xl`}
             style={{
               boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'
             }}>
          {message}
        </div>
      )}
      <p className={`${isMobile ? 'mt-3' : 'mt-4'} text-center text-white/70 ${isMobile ? 'text-sm' : 'text-base'}`}>Try Email Relays & Second Number for Free.</p>
      <p className={`${isMobile ? 'mt-2' : 'mt-3'} text-center text-white/50 ${isMobile ? 'text-xs' : 'text-sm'}`}>
        By signing up, you agree to our{' '}
        <a 
          href="https://www.chatodyssey.com/terms-of-service" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-300 hover:text-blue-200 transition-colors underline"
        >
          Terms of Service
        </a>
      </p>
    </>
  );

  return (
    <div 
      className="min-h-screen text-white flex flex-col"
      style={{
        background: 'linear-gradient(to bottom right, #0d0520, #1a0b2e, #0a0615, #050208)'
      }}
    >
      <div className={`flex flex-col items-center justify-center p-4 ${isMobile ? 'pb-4' : 'min-h-screen py-8'}`}>
        <div className="text-center mb-6">
          <img
            src="/images/logowhite.png"
            alt="ChatOdyssey Logo"
            className={`mx-auto object-contain ${isMobile ? 'mb-4 w-16 h-16' : 'mb-6 w-20 h-20'}`}
            style={{ filter: 'drop-shadow(0 0 12px rgba(255,255,255,0.2))' }}
          />
          {!isMobile && (
            <h1 className="text-3xl font-light text-white mb-2">
              {isLogin ? 'Welcome Back' : 'Join ChatOdyssey'}
            </h1>
          )}
          {!isMobile && (
            <p className="text-white/60 text-base">
              {isLogin ? 'Sign in to access your virtual identities' : 'Create your account to get started'}
            </p>
          )}
        </div>

        {!isMobile && (
          <div className="w-full max-w-md mx-auto">
            {loginContent}
          </div>
        )}
      </div>
      
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 w-full bg-black/30 backdrop-blur-2xl border-t border-white/10 rounded-t-3xl shadow-2xl z-50"
             style={{
               boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 -8px 32px rgba(0,0,0,0.4)',
               maxHeight: '85vh'
             }}>
          <div className="text-center p-4 pb-2">
            <h2 className="text-xl font-light text-white mb-1">
              {isLogin ? 'Welcome Back' : 'Join ChatOdyssey'}
            </h2>
            <p className="text-white/60 text-sm">
              {isLogin ? 'Sign in to continue' : 'Create your account'}
            </p>
          </div>
          <div className="px-4 pb-4 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 80px)' }}>
            {loginContent}
          </div>
        </div>
      )}
    </div>
  );
}